import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/browser';
import { share, tap } from 'rxjs/operators';
import { env } from '../../environments/environment';
import { User } from '../shared/interfaces';
import { Permission } from '../shared/permissions';
import { Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(protected http: HttpClient) {}

  cachedProfile: User;
  private cachedSettings: any;
  public async profile() {
    if (!this.cachedProfile) {
      this.cachedProfile = await this.http.get<User>(`${env.APIUrl}/user/me`).toPromise();
      Sentry.setUser({
        email: this.cachedProfile.email,
        username: this.cachedProfile.name,
      });
    }
    return this.cachedProfile;
  }

  public login(post) {
    const response = this.http.post<any>(`${env.APIUrl}/auth/login`, post).pipe(share());

    return response;
  }

  public emailAvailable(email: string) {
    return this.http.get<boolean>(`${env.APIUrl}/user/emailAvailable`, { params: { email: email } as any });
  }

  public register(post) {
    return this.http.post<any>(`${env.APIUrl}/user/register`, post);
  }
  public count(post) {
    return this.http.get<any>(`${env.APIUrl}/user/adminCount`, { params: post as any }).toPromise();
  }

  public listAdmins(parms) {
    return this.http.get<any>(`${env.APIUrl}/user/admins`, { params: parms as any }).toPromise();
  }

  public assignees() {
    return this.http.get<any>(`${env.APIUrl}/user/assignees`).toPromise();
  }

  public listAdminWithRecletter() {
    return this.http.get<any[]>(`${env.APIUrl}/user/admins-rec-letter`);
  }

  public get(id: number) {
    return this.http.get<User>(`${env.APIUrl}/user/${id}`).toPromise();
  }

  public edit(id: number, user: User) {
    return this.http.put<User>(`${env.APIUrl}/user/${id}`, user).toPromise();
  }

  public archive(id: number) {
    return this.http.put<User>(`${env.APIUrl}/user/${id}/archive`, null).toPromise();
  }

  public restore(id: number) {
    return this.http.put<User>(`${env.APIUrl}/user/${id}/restore`, null).toPromise();
  }

  public create(user: User) {
    return this.http.post<User>(`${env.APIUrl}/user`, user).toPromise();
  }

  public async hasPermissions(requiredPermissions: Permission[]) {
    const user = await this.profile();

    if (!user) {
      return false;
    }

    const userPermissions = (user.roles || []).reduce((perms, curr) => {
      perms.push(...curr.permissions);
      return perms;
    }, []);

    let ret = true;

    (requiredPermissions || [])
      .map(p => p.key)
      .forEach(p => {
        if (userPermissions.indexOf(p) >= 0) {
          // User has permission
        } else {
          console.log('Missing permission:', p);
          ret = false;
        }
      });

    return ret;
  }

  async getSetting(key: string): Promise<any> {
    if (this.cachedSettings && key in this.cachedSettings) {
      return this.cachedSettings[key];
    }

    try {
      const response = await this.http.get<Record<string, any>>(`${env.APIUrl}/User/settings`).toPromise();
      this.cachedSettings = response || {};
      return this.cachedSettings[key];
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      return undefined;
    }
  }

  saveSetting(key: string, value: any): Observable<any> {
    if (!this.cachedSettings) {
      this.cachedSettings = {};
    }
    this.cachedSettings[key] = value;
    return this.http.post(`${env.APIUrl}/User/settings`, { key, value });
  }

  clearCache(): void {
    this.cachedSettings = null;
  }

  public async hasFeatures(requiredFeatures: Permission[]) {
    const user = await this.profile();

    if (!user) {
      return false;
    }

    const orgFeatures = user.organization.features.map(f => f.key);

    let ret = true;

    requiredFeatures
      .map(p => p.key)
      .forEach(p => {
        if (orgFeatures.indexOf(p) >= 0) {
          // Org has feature
        } else {
          console.log('Missing feature:', p);
          ret = false;
        }
      });

    return ret;
  }
}
